/**
 * The rows a control chart draws (pure), built from the server's series or from plain points with explicit
 * limits. Nothing is estimated here: every center line and limit is the server's (or the caller's).
 *
 *   - a null value stays null, so the line breaks at a gap (a stuck reading, or no data);
 *   - a point before the baseline ("pre") moves to its own muted line, which runs into the first point after it;
 *   - a signal is matched to its point by time; one on another sub-chart (the reading-to-reading change, the
 *     range within the hour) names that sub-chart;
 *   - an EWMA or CUSUM chart plots its statistic against the statistic's own limits, never the raw values
 *     (a CUSUM's decision interval is not on the readings' scale).
 */
import {
  spcLimitsHiddenText,
  spcLocationSub,
  spcRuleLabel,
  spcSubChartLabel,
  type SpcSeries,
  type SpcSignal,
} from "@/lib/api/spc";

export interface SpcPlotRow {
  /** Position on the x axis: epoch ms on a time axis, else the point's own label. */
  x: number | string;
  at?: string;
  /** The value on the main line; null breaks the line. */
  v: number | null;
  /** A value before the baseline, drawn muted; the first point after it repeats here so the lines meet. */
  vPre: number | null;
  /** CUSUM: the lower sum, drawn as a second line. */
  lower: number | null;
  center: number | null;
  ucl: number | null;
  lcl: number | null;
  phase?: "pre" | "I" | "II";
  censored?: "ceiling" | "floor";
  /** Why a point has no value. */
  gap?: "stuck" | "no_data";
  /** Beyond the limits, or carrying a stored signal. */
  flagged: boolean;
  /** The rules broken at this point, in words. */
  signals: string[];
}

export interface SpcPlot {
  kind: "location" | "ewma" | "cusum";
  rows: SpcPlotRow[];
  timeAxis: boolean;
  hasLimits: boolean;
  hasPre: boolean;
  hasLower: boolean;
  hasCensored: boolean;
  /** The baseline window, clipped to the plotted span. */
  band: { x1: number; x2: number } | null;
}

export const BEYOND_LIMITS = "Past the control limits";

/** Signal labels by time (ms). */
function signalsByTime(signals: readonly SpcSignal[], loc: string): Map<number, string[]> {
  const out = new Map<number, string[]>();
  for (const s of signals) {
    const t = Date.parse(s.at);
    if (Number.isNaN(t)) continue;
    const rule = spcRuleLabel(s.rule);
    const label = s.subject_key === loc ? rule : `${spcSubChartLabel(s.subject_key)}: ${rule}`;
    const at = out.get(t) ?? [];
    if (!at.includes(label)) at.push(label);
    out.set(t, at);
  }
  return out;
}

function summarize(kind: SpcPlot["kind"], rows: SpcPlotRow[], band: SpcPlot["band"], timeAxis: boolean): SpcPlot {
  return {
    kind,
    rows,
    timeAxis,
    band,
    hasLimits: rows.some((r) => r.ucl !== null && r.lcl !== null),
    hasPre: rows.some((r) => r.phase === "pre" && r.vPre !== null),
    hasLower: rows.some((r) => r.lower !== null),
    hasCensored: rows.some((r) => r.censored !== undefined),
  };
}

/**
 * The server's series as chart rows. A port's series whose limits are in another unit than its points
 * (`spcLimitsHiddenText`) plots its points alone (`spcPointsOnlyPlot`).
 */
export function spcSeriesPlot(series: SpcSeries): SpcPlot {
  if (spcLimitsHiddenText(series) !== null) return spcPointsOnlyPlot(series);
  const type = series.chart.chart_type;
  const byTime = signalsByTime(series.signals ?? [], spcLocationSub(type));
  let rows: SpcPlotRow[];
  let kind: SpcPlot["kind"] = "location";
  if (type === "ewma" || type === "cusum") {
    kind = type;
    rows = (series.statistic?.points ?? []).map((p) => {
      const t = Date.parse(p.at);
      const signals = byTime.get(t) ?? [];
      return {
        x: t, at: p.at, v: p.value, vPre: null, lower: p.c_minus ?? null,
        center: type === "ewma" ? series.limits.center : null, ucl: p.ucl, lcl: p.lcl, phase: "II",
        flagged: p.beyond_limits || signals.length > 0, signals,
      };
    });
  } else {
    rows = series.points.map((p) => {
      const t = Date.parse(p.at);
      const signals = [...(byTime.get(t) ?? [])];
      if (p.beyond_limits && !signals.some((s) => s.startsWith(BEYOND_LIMITS))) signals.unshift(BEYOND_LIMITS);
      const pre = p.phase === "pre";
      return {
        x: t, at: p.at, v: pre ? null : p.value, vPre: pre ? p.value : null, lower: null,
        center: p.center, ucl: p.ucl, lcl: p.lcl, phase: p.phase, censored: p.censored,
        gap: p.value === null ? (p.excluded ?? "no_data") : undefined,
        flagged: p.value !== null && (p.beyond_limits || signals.length > 0), signals,
      };
    });
    const firstAfter = rows.findIndex((r) => r.phase !== "pre");
    if (firstAfter > 0) rows[firstAfter].vPre = rows[firstAfter].v;
  }
  const xs = rows.map((r) => r.x as number).filter((x) => Number.isFinite(x));
  let band: SpcPlot["band"] = null;
  if (xs.length > 0) {
    const x1 = Math.max(Math.min(...xs), Date.parse(series.phase_one?.from ?? series.limits.baseline_from));
    const x2 = Math.min(Math.max(...xs), Date.parse(series.phase_one?.to ?? series.limits.baseline_to));
    if (x1 < x2) band = { x1, x2 };
  }
  return summarize(kind, rows, band, true);
}

/**
 * The points of a port's series whose limits are in another unit: on a time axis, with no center line, no limits, no
 * baseline band and nothing flagged, since each of those was set in the other unit. An EWMA or CUSUM chart plots its
 * readings here, not its statistic, which is reckoned from the same limits.
 */
function spcPointsOnlyPlot(series: SpcSeries): SpcPlot {
  const rows = series.points.map(
    (p): SpcPlotRow => ({
      x: Date.parse(p.at),
      at: p.at,
      v: p.value,
      vPre: null,
      lower: null,
      center: null,
      ucl: null,
      lcl: null,
      phase: p.phase,
      censored: p.censored,
      gap: p.value === null ? (p.excluded ?? "no_data") : undefined,
      flagged: false,
      signals: [],
    }),
  );
  return summarize("location", rows, null, true);
}

/** Plain points, with limits only when the caller gives all three. */
export function spcPointsPlot(
  points: readonly { x: string | number; v: number }[],
  limits?: { center: number; ucl: number; lcl: number },
): SpcPlot {
  const rows = points.map((p): SpcPlotRow => {
    const beyond = limits !== undefined && (p.v > limits.ucl || p.v < limits.lcl);
    return {
      x: p.x, v: p.v, vPre: null, lower: null,
      center: limits?.center ?? null, ucl: limits?.ucl ?? null, lcl: limits?.lcl ?? null,
      flagged: beyond, signals: beyond ? [BEYOND_LIMITS] : [],
    };
  });
  return summarize("location", rows, null, false);
}

/** A time-axis tick: the hour within two days, else the date. */
export function spcTimeTick(ms: number, spanMs: number): string {
  const d = new Date(ms);
  return spanMs <= 2 * 86_400_000
    ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The x axis of a plot on a time axis (epoch ms, ticks in the viewer's time); none for plain points. */
export function spcTimeAxis(plot: SpcPlot) {
  if (!plot.timeAxis) return {};
  const rows = plot.rows;
  const span = rows.length > 1 ? (rows[rows.length - 1].x as number) - (rows[0].x as number) : 0;
  return {
    type: "number" as const,
    scale: "time" as const,
    domain: ["dataMin", "dataMax"],
    minTickGap: 24,
    tickFormatter: (ms: number) => spcTimeTick(ms, span),
  };
}

/** A time in the viewer's words: "Sep 24, 10:05 AM". */
export function spcWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** A chart value: a few significant digits below 1 (a rate), one or two decimals above. */
export function spcNum(v: number): string {
  const a = Math.abs(v);
  return a !== 0 && a < 1
    ? v.toLocaleString(undefined, { maximumSignificantDigits: 3 })
    : v.toLocaleString(undefined, { maximumFractionDigits: a >= 100 ? 1 : 2 });
}
